// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: feedback on Konvo itself ("rate my app"). Staff ask from Team insights; Konvo posts the
// ask in the person's Konvo DM with a card (a star rating, what kind of note, which part of Konvo,
// a text box). People answer on the card, by writing in the Konvo DM, or by telling their
// assistant (send_app_feedback). Everything lands on Team insights → Feedback.
// The API and the app both import these shapes.

import {z} from 'zod';

/** Embed provider of the ask card. Its one field is `card: <ask_id>`; older apps show title and description. */
export const FEEDBACK_CARD_PROVIDER = 'Konvo:feedback';

/** The parts of Konvo a note can be about. */
export const FEEDBACK_AREAS = [
	'chats',
	'mail',
	'calendar',
	'tasks',
	'whatsapp',
	'home',
	'notes',
	'calls',
	'assistant',
	'phone_app',
	'other',
] as const;
export type FeedbackArea = (typeof FEEDBACK_AREAS)[number];

/** idea: a feature request or improvement; problem: something broken or annoying; praise: something they like. */
export const FEEDBACK_KINDS = ['idea', 'problem', 'praise'] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

/** card: answered on the ask card; reply: written in the Konvo DM; assistant: told their assistant. */
export const FEEDBACK_SOURCES = ['card', 'reply', 'assistant'] as const;
export type FeedbackSource = (typeof FEEDBACK_SOURCES)[number];

/** One ask per person in this many days. */
export const FEEDBACK_ASK_EVERY_DAYS = 7;
/** An ask stays open (answerable on the card, replies in the DM count) this long. */
export const FEEDBACK_ASK_OPEN_DAYS = 14;

export const FEEDBACK_TEXT_MAX = 2000;

/** POST /konvo/insights/feedback/asks (staff). */
export const SendFeedbackAskSchema = z.object({
	user_ids: z.array(z.string().max(40)).min(1).max(200),
	/** Konvo's message above the card; `{first_name}` becomes each person's first name. Empty: the default. */
	text: z.string().max(1500).optional(),
	/** What the card asks after the rating; empty: the default for the focus. */
	question: z.string().max(200).optional(),
	/** Ask about one part of Konvo (pre-selects it on the card), or null for anything. */
	area: z.enum(FEEDBACK_AREAS).nullable().default(null),
	/** Show the 1–5 star rating first. */
	rating: z.boolean().default(true),
});
export type SendFeedbackAsk = z.infer<typeof SendFeedbackAskSchema>;

/** skipped: asked_recently | opted_out | not_found; failed: error. */
export const FEEDBACK_ASK_RESULT_REASONS = ['asked_recently', 'opted_out', 'not_found', 'error'] as const;

export const SendFeedbackAskResultSchema = z.object({
	results: z.array(
		z.object({
			user_id: z.string(),
			status: z.enum(['sent', 'skipped', 'failed']),
			reason: z.enum(FEEDBACK_ASK_RESULT_REASONS).nullable(),
			ask_id: z.string().nullable(),
		}),
	),
});
export type SendFeedbackAskResult = z.infer<typeof SendFeedbackAskResultSchema>;

/** POST /konvo/feedback/asks/:askId/answers, and the assistant's send_app_feedback. */
export const FeedbackAnswerInputSchema = z
	.object({
		rating: z.number().int().min(1).max(5).nullable().default(null),
		kind: z.enum(FEEDBACK_KINDS).nullable().default(null),
		area: z.enum(FEEDBACK_AREAS).nullable().default(null),
		text: z.string().max(FEEDBACK_TEXT_MAX).default(''),
	})
	.refine((input) => input.rating != null || input.text.trim().length > 0, {message: 'rating or text required'});
export type FeedbackAnswerInput = z.infer<typeof FeedbackAnswerInputSchema>;

export const FeedbackAnswerSchema = z.object({
	answer_id: z.string(),
	ask_id: z.string().nullable(),
	at: z.string(),
	rating: z.number().int().min(1).max(5).nullable(),
	kind: z.enum(FEEDBACK_KINDS).nullable(),
	area: z.enum(FEEDBACK_AREAS).nullable(),
	text: z.string(),
	source: z.enum(FEEDBACK_SOURCES),
});
export type FeedbackAnswer = z.infer<typeof FeedbackAnswerSchema>;

/** GET /konvo/feedback/asks/:askId (the person asked): what the card shows. */
export const FeedbackAskViewSchema = z.object({
	ask_id: z.string(),
	asked_at: z.string(),
	question: z.string(),
	area: z.enum(FEEDBACK_AREAS).nullable(),
	rating: z.boolean(),
	/** Answers can still be given (FEEDBACK_ASK_OPEN_DAYS). */
	open: z.boolean(),
	/** Their answers to this ask, oldest first. */
	answers: z.array(FeedbackAnswerSchema),
});
export type FeedbackAskView = z.infer<typeof FeedbackAskViewSchema>;

const PersonRef = z.object({
	user_id: z.string(),
	name: z.string(),
	avatar: z.string().nullable(),
});

/** GET /konvo/insights/feedback (staff): everything people said, and who was asked. */
export const InsightsFeedbackSchema = z.object({
	/** Asks sent in the last 90 days. */
	asked: z.number().int(),
	/** People who answered one of those asks. */
	answered: z.number().int(),
	/** Mean of every rating (null without ratings), and how many of each 1..5. */
	avg_rating: z.number().nullable(),
	ratings: z.array(z.number().int()).length(5),
	items: z.array(FeedbackAnswerSchema.extend({person: PersonRef})),
	asks: z.array(
		z.object({
			ask_id: z.string(),
			person: PersonRef,
			asked_at: z.string(),
			area: z.enum(FEEDBACK_AREAS).nullable(),
			read: z.boolean(),
			answers: z.number().int(),
		}),
	),
	/** When each person was last asked (people never asked are absent). */
	last_asked: z.record(z.string(), z.string()),
});
export type InsightsFeedback = z.infer<typeof InsightsFeedbackSchema>;
export type InsightsFeedbackItem = InsightsFeedback['items'][number];

/** The ask card's embed for the Konvo DM: readable as a plain card in apps that predate it. */
export function feedbackAskEmbed(askId: string, title: string, description: string): Record<string, unknown> {
	return {
		provider: {name: FEEDBACK_CARD_PROVIDER},
		author: {name: 'Konvo'},
		title,
		description,
		color: 0x9981f4,
		fields: [{name: 'card', value: askId, inline: false}],
	};
}

/** The ask id of a feedback card embed, or null. */
export function feedbackAskId(embed: {
	provider?: {name?: string | null} | null;
	fields?: ReadonlyArray<{name?: string | null; value?: string | null}> | null;
}): string | null {
	if (embed.provider?.name !== FEEDBACK_CARD_PROVIDER) return null;
	const value = embed.fields?.find((field) => field.name === 'card')?.value?.trim();
	return value && /^[a-z0-9]{6,40}$/.test(value) ? value : null;
}
